/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// ─── Compact Toast Notification System ────────────────────────────────────────
// Matches BackgroundTaskBar design — compact, bottom-right, unified style.
// Usage: Wrap app in <ToastProvider>, then call useToast() in any component.

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from './utils';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  variant: ToastVariant;
  title: string;
  message?: string;
  duration: number;
  createdAt: number;
}

interface ToastContextValue {
  success: (title: string, message?: string) => void;
  error:   (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info:    (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

const VARIANT_CONFIG: Record<ToastVariant, {
  icon: React.ElementType;
  iconColor: string;
  textColor: string;
  bg: string;
  border: string;
}> = {
  success: { icon: CheckCircle2, iconColor: 'text-emerald-500', textColor: 'text-emerald-700', bg: 'bg-emerald-50/95', border: 'border-emerald-200' },
  error:   { icon: XCircle,      iconColor: 'text-red-500',     textColor: 'text-red-700',     bg: 'bg-red-50/95',     border: 'border-red-200' },
  warning: { icon: AlertTriangle, iconColor: 'text-amber-500',   textColor: 'text-amber-700',   bg: 'bg-amber-50/95',   border: 'border-amber-200' },
  info:    { icon: Info,          iconColor: 'text-sky-500',     textColor: 'text-sky-700',     bg: 'bg-sky-50/95',     border: 'border-sky-200' },
};

const DEFAULT_DURATIONS: Record<ToastVariant, number> = {
  success: 3000,
  error:   0,       // sticky — user must dismiss
  warning: 4000,
  info:    3000,
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const cfg = VARIANT_CONFIG[toast.variant];
  const Icon = cfg.icon;
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (toast.duration > 0) {
      timerRef.current = setTimeout(() => {
        setExiting(true);
        setTimeout(() => onDismiss(toast.id), 200);
      }, toast.duration);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [toast.id, toast.duration, onDismiss]);

  const handleDismiss = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setExiting(true);
    setTimeout(() => onDismiss(toast.id), 200);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 pl-3 pr-2 py-2.5 rounded-xl border shadow-lg backdrop-blur-sm transition-all duration-200 max-w-xs w-full',
        cfg.bg, cfg.border,
        exiting ? 'opacity-0 translate-y-2 scale-95' : 'opacity-100 translate-y-0 scale-100',
      )}
      role="alert"
    >
      <div className={cn('shrink-0', cfg.iconColor)}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn('text-xs font-semibold break-words leading-snug', cfg.textColor)}>{toast.title}</p>
        {toast.message && <p className="text-[10px] text-zinc-400 whitespace-pre-wrap break-words leading-snug">{toast.message}</p>}
      </div>
      <button onClick={handleDismiss} className="shrink-0 p-0.5 text-slate-400 hover:text-slate-600 transition-colors">
        <X size={12} />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const push = useCallback((variant: ToastVariant, title: string, message?: string) => {
    const t: Toast = {
      id: crypto.randomUUID(),
      variant,
      title,
      message,
      duration: DEFAULT_DURATIONS[variant],
      createdAt: Date.now(),
    };
    setToasts(prev => [...prev, t].slice(-5));
  }, []);

  const api: ToastContextValue = {
    success: useCallback((t, m) => push('success', t, m), [push]),
    error:   useCallback((t, m) => push('error',   t, m), [push]),
    warning: useCallback((t, m) => push('warning', t, m), [push]),
    info:    useCallback((t, m) => push('info',    t, m), [push]),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed top-[72px] right-4 z-[9999] flex flex-col gap-2 pointer-events-none max-w-xs w-full">
        {toasts.map(t => (
          <div key={t.id} className="pointer-events-auto">
            <ToastItem toast={t} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
